import { useCallback, useEffect, useRef, useState } from 'react'
import type { ActiveStream, TrassirCamera, TrassirWsMessage } from './types'
import { trassirWsUrl } from './utils'

const WS_RECONNECT_MAX = 5
const WS_RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000]
const WS_CONNECT_TIMEOUT_MS = 15000
/** Application heartbeat — nginx/ingress считает idle по трафику (браузер не шлёт WS ping). */
const WS_HEARTBEAT_MS = 25000

export function useTrassirControlWs(serverId: number | null, enabled: boolean) {
  const wsRef = useRef<WebSocket | null>(null)
  const connectGenRef = useRef(0)
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const pendingGuidRef = useRef<string | null>(null)
  const pendingLiveOptsRef = useRef<{
    stream?: 'main' | 'sub'
    scale?: string | null
    q?: number
  } | null>(null)
  const modeRef = useRef<'live' | 'archive'>('live')

  const [connected, setConnected] = useState(false)
  const [status, setStatus] = useState('Выберите сервер')
  const [cameras, setCameras] = useState<TrassirCamera[]>([])
  const [activeStream, setActiveStream] = useState<ActiveStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [archiveEndSeq, setArchiveEndSeq] = useState(0)

  const stopPlayers = useCallback(() => {
    setActiveStream(null)
  }, [])

  const clearHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current)
      heartbeatTimerRef.current = null
    }
  }, [])

  const closeWs = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current)
      connectTimeoutRef.current = null
    }
    clearHeartbeat()
    connectGenRef.current += 1
    reconnectAttemptsRef.current = 0
    const ws = wsRef.current
    if (ws) {
      try {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'stop' }))
        }
        ws.onclose = null
        ws.onerror = null
        ws.close()
      } catch {
        /* ignore */
      }
      wsRef.current = null
    }
    setConnected(false)
    stopPlayers()
  }, [stopPlayers, clearHeartbeat])

  const scheduleReconnect = useCallback(() => {
    if (!enabled || !serverId || reconnectTimerRef.current) return
    if (reconnectAttemptsRef.current >= WS_RECONNECT_MAX) {
      setStatus('Соединение потеряно')
      closeWs()
      return
    }
    const delay = WS_RECONNECT_DELAYS[Math.min(reconnectAttemptsRef.current, WS_RECONNECT_DELAYS.length - 1)]
    reconnectAttemptsRef.current += 1
    setStatus(`Переподключение… (${reconnectAttemptsRef.current}/${WS_RECONNECT_MAX})`)
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null
      connect(false)
    }, delay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, serverId, closeWs])

  const handleMessage = useCallback(
    (data: TrassirWsMessage) => {
      switch (data.type) {
        case 'cameras':
          setCameras(data.data || [])
          // Не затираем статус архива сообщением «Выберите камеру»
          if (modeRef.current !== 'archive') {
            setStatus(data.data?.length ? 'Выберите камеру' : 'Камеры не найдены')
          }
          if (pendingGuidRef.current && wsRef.current?.readyState === WebSocket.OPEN && modeRef.current === 'live') {
            const payload: Record<string, unknown> = { guid: pendingGuidRef.current }
            const opts = pendingLiveOptsRef.current
            if (opts?.stream) payload.stream = opts.stream
            if (opts?.scale != null && opts.scale !== '') payload.scale = opts.scale
            if (opts?.scale === null) payload.scale = null
            if (opts?.q != null && Number.isFinite(opts.q)) payload.q = opts.q
            wsRef.current.send(JSON.stringify(payload))
          }
          break
        case 'stream':
          setActiveStream({
            streamId: data.streamId,
            mode: data.mode === 'archive' ? 'archive' : 'live',
          })
          setStatus(data.mode === 'archive' ? 'Воспроизведение архива' : 'Видеопоток активен')
          setError(null)
          break
        case 'archive_end':
          if (data.reason === 'complete') {
            setStatus('Воспроизведение завершено')
            setError(null)
          } else if (data.reason === 'timeout') {
            setStatus('Архив: нет кадров')
            setError(data.message || 'Нет видеокадров из архива')
          } else {
            setStatus('Ошибка воспроизведения')
            setError(data.message || 'Ошибка воспроизведения архива')
          }
          if (modeRef.current === 'archive') {
            stopPlayers()
            setArchiveEndSeq((n) => n + 1)
          }
          break
        case 'error':
          setError(data.details ? `${data.message}: ${data.details}` : data.message)
          setStatus(`Ошибка: ${data.message}`)
          break
        case 'pong':
          break
        default:
          break
      }
    },
    [stopPlayers],
  )

  const connect = useCallback(
    (manual = true) => {
      if (!serverId || !enabled) return

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      if (manual) reconnectAttemptsRef.current = 0

      const prev = wsRef.current
      if (prev) {
        prev.onclose = null
        prev.onerror = null
        prev.close()
        wsRef.current = null
      }

      clearHeartbeat()
      stopPlayers()
      setCameras([])
      setError(null)
      setStatus('Подключение…')

      const gen = ++connectGenRef.current
      const ws = new WebSocket(trassirWsUrl(serverId))
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws

      connectTimeoutRef.current = setTimeout(() => {
        if (connectGenRef.current !== gen) return
        if (ws.readyState === WebSocket.CONNECTING) {
          setError('Таймаут подключения к TRASSIR WebSocket')
          setStatus('Не удалось подключиться (таймаут)')
          try {
            ws.onclose = null
            ws.close()
          } catch {
            /* ignore */
          }
        }
      }, WS_CONNECT_TIMEOUT_MS)

      ws.onopen = () => {
        if (connectGenRef.current !== gen) return
        if (connectTimeoutRef.current) {
          clearTimeout(connectTimeoutRef.current)
          connectTimeoutRef.current = null
        }
        reconnectAttemptsRef.current = 0
        clearHeartbeat()
        heartbeatTimerRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            try {
              wsRef.current.send(JSON.stringify({ type: 'ping' }))
            } catch {
              /* ignore */
            }
          }
        }, WS_HEARTBEAT_MS)
        setConnected(true)
        setStatus('Загрузка списка камер…')
      }

      ws.onmessage = (event) => {
        if (connectGenRef.current !== gen) return
        if (typeof event.data !== 'string') return
        try {
          handleMessage(JSON.parse(event.data) as TrassirWsMessage)
        } catch {
          /* ignore */
        }
      }

      ws.onerror = () => {
        if (connectGenRef.current !== gen) return
        if (connectTimeoutRef.current) {
          clearTimeout(connectTimeoutRef.current)
          connectTimeoutRef.current = null
        }
        clearHeartbeat()
        setStatus('Ошибка подключения')
        setError('Ошибка WebSocket TRASSIR')
        setConnected(false)
      }

      ws.onclose = () => {
        if (connectGenRef.current !== gen) return
        if (connectTimeoutRef.current) {
          clearTimeout(connectTimeoutRef.current)
          connectTimeoutRef.current = null
        }
        clearHeartbeat()
        wsRef.current = null
        setConnected(false)
        if (enabled && serverId && modeRef.current === 'live') {
          scheduleReconnect()
        } else {
          stopPlayers()
        }
      }
    },
    [serverId, enabled, handleMessage, scheduleReconnect, stopPlayers, clearHeartbeat],
  )

  useEffect(() => {
    if (!serverId || !enabled) {
      closeWs()
      setCameras([])
      setStatus('Выберите сервер')
      return
    }
    connect(true)
    return () => {
      closeWs()
    }
    // Only reconnect when server or enabled flag changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId, enabled])

  const selectLiveCamera = useCallback(
    (
      guid: string,
      opts?: {
        stream?: 'main' | 'sub'
        scale?: string | null
        q?: number
      },
    ) => {
      modeRef.current = 'live'
      pendingGuidRef.current = guid
      pendingLiveOptsRef.current = opts || null
      stopPlayers()
      setStatus('Подключение к камере…')
      setError(null)
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        setError('WebSocket не подключен')
        return false
      }
      const payload: Record<string, unknown> = { guid }
      if (opts?.stream) payload.stream = opts.stream
      if (opts?.scale != null && opts.scale !== '') payload.scale = opts.scale
      if (opts?.scale === null) payload.scale = null
      if (opts?.q != null && Number.isFinite(opts.q)) payload.q = opts.q
      ws.send(JSON.stringify(payload))
      return true
    },
    [stopPlayers],
  )

  const startArchivePlayback = useCallback(
    (
      guid: string,
      range:
        | string
        | {
            start: string
            stop: string
            startUs?: number
            stopUs?: number
            localAsUtc?: boolean
          },
      stopArg?: string,
    ) => {
      modeRef.current = 'archive'
      stopPlayers()
      setStatus('Подключение к архиву…')
      setError(null)
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        setError('WebSocket не подключен')
        return false
      }
      const payload =
        typeof range === 'string'
          ? { type: 'archive' as const, guid, start: range, stop: stopArg }
          : {
              type: 'archive' as const,
              guid,
              start: range.start,
              stop: range.stop,
              startUs: range.startUs,
              stopUs: range.stopUs,
              ...(range.localAsUtc ? { localAsUtc: true } : {}),
            }
      // stop live делает бэкенд (stopStreamsForGuid); отдельный type:stop давал гонку WS
      ws.send(JSON.stringify(payload))
      return true
    },
    [stopPlayers],
  )

  const stopStream = useCallback(() => {
    stopPlayers()
    const ws = wsRef.current
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'stop' }))
    }
    setStatus(cameras.length ? 'Выберите камеру' : 'Камеры не найдены')
  }, [cameras.length, stopPlayers])

  return {
    connected,
    status,
    cameras,
    activeStream,
    error,
    archiveEndSeq,
    selectLiveCamera,
    startArchivePlayback,
    stopStream,
    reconnect: () => connect(true),
    closeWs,
  }
}
